import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";

import store from "./redux/store";

import "./index.css";

import AppPage from "./App";

import DashboardPage from "./pages/dashboard";

import ClientsPage from "./pages/client/clients";
import ClientPage from "./pages/client/client";

import AuthGuard from "./guards/AuthGuard";
import LoginPage from "./pages/auth/login";
import GuestGuard from "./guards/GuestGuard";
import OperatorPage from "./pages/operators/operators.page";
import ClientSpaceList from "./pages/spaces/space-client-list";

import AjaxUsersPage from "./pages/ajax-users/AjaxUsers";
import { ClientHubPage } from "./pages/client-hub/client.hub";
import { ClientHubCamera } from "./pages/client-hub-cam/client-hub.cam";
import CameraDetailInfo from "./pages/client-hub-cam/cam.detail";
import GroupsTable from "./pages/client-hub-cam/device.control";

import "./services/axiosInterceptor";
import { ClientHubAsi } from "./pages/client-hub-asi/client-hub-asi";
import UserHubASI from "./pages/client-hub-asi/users-hub-asi";
import DevicesPage from "./pages/devices/devices.page";
import { DeviceSettingsPage } from "./pages/devices/device.settings";
import DevicesAsiByHubPage from "./pages/devices/devices.asi-by-hub";
import RoleGuard from "./guards/RoleGuard";
import { I18nProvider } from "./i18n/I18nContext";

const router = createBrowserRouter([
  {
    path: "/sign-in",
    element: (
      <GuestGuard>
        <LoginPage />
      </GuestGuard>
    ),
  },
  {
    path: "",
    element: (
      <AuthGuard>
        <AppPage />
      </AuthGuard>
    ),
    children: [
      {
        path: "/main",
        element: <DashboardPage />,
      },
      {
        path: "/clients",
        element: <ClientsPage />,
      },
      {
        path: "/client/:id",
        element: <ClientPage />,
      },
      {
        path: "/client-spaces/:id",
        element: <ClientSpaceList />,
      },
      {
        path: "/space-hubs/:id",
        element: <ClientHubPage />,
      },
      {
        path: "/space/:id/hub-cam/:id",
        element: <ClientHubCamera />,
      },
      {
        path: "/space/:id/hub-asi/:id",
        element: <ClientHubAsi />,
      },
      {
        path: "/hub-asi/:id/asi-people/:id",
        element: <UserHubASI />,
      },
      {
        path: "/devices",
        element: (
          <RoleGuard roles={["admin", "operator", "manager"]}>
            <DevicesPage />
          </RoleGuard>
        ),
      },
      {
        path: "/devices/hub/:hubExternalId",
        element: (
          <RoleGuard roles={["admin", "operator", "manager"]}>
            <DevicesAsiByHubPage />
          </RoleGuard>
        ),
      },
      {
        path: "/devices/:asiId",
        element: (
          <RoleGuard roles={["admin", "operator", "manager"]}>
            <DeviceSettingsPage />
          </RoleGuard>
        ),
      },
      {
        path: "/cam-info/:id",
        element: <CameraDetailInfo />,
      },
      {
        path: "/cam-info-table/:id",
        element: <GroupsTable />,
      },
      {
        path: "/operators",
        element: (
          <RoleGuard roles={["admin", "manager"]}>
            <OperatorPage />
          </RoleGuard>
        ),
      },
      {
        path: "/ajax-users",
        element: <AjaxUsersPage />,
      },
    ],
  },
]);

const persistor = persistStore(store);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <I18nProvider>
          <RouterProvider router={router} />
        </I18nProvider>
      </PersistGate>
    </Provider>
  </React.StrictMode>
);
